import React, { useState, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef, ReactZoomPanPinchState } from 'react-zoom-pan-pinch';
import VideoPlayer, { ProgressState } from '../VideoPlayer/VideoPlayer';
import { Typography, makeStyles } from '@material-ui/core';
import { VideoOverlayObject, Coordinate } from './VideoTypes';
import { ClickInfo, MouseButton } from '../Sessions/Label/useClickHandler';
import useInterval from '../Util/useInterval';
import ViewOverlayCanvas from './ViewOverlayCanvas';
import { PanMethod } from './VideoLayout';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    backgroundColor: "#444",
    borderRight: "1px dashed #ff5a0f66",
    borderLeft: "1px dashed #ff5a0f66",
  },
});

type Props = {
  id: string;
  viewWidth: number;
  viewHeight: number;
  videoWidth: number;
  videoHeight: number;
  url: string;
  playTime?: number;
  syncOffset?: number;
  updatePlayTime?: boolean;
  playbackSpeed?: number;
  isPlaying: boolean;
  overlayItems: VideoOverlayObject[];
  mouseOverlay?: VideoOverlayObject[];
  onMouseDown?: (id: string, info: ClickInfo) => void;
  onMouseUp?: (id: string, info: ClickInfo) => void;
  onMouseMove?: (id: string, coord: Coordinate | undefined) => void;
  onChangeVideoDuration?: (duration: number, syncOffset: number) => void;
  intervalMs?: number;
  onVideoProgress?: (id: string, p: ProgressState) => void;
  isFullscreen?: boolean;
  sharedZoom?: number;
  onSharedZoomChange?: (id: string, newZoom: number) => void;
  onChangeVideoPlaying?: (id: string, isPlaying: boolean) => void;
  onChangeVideoLoading?: (id: string, isLoading: boolean) => void;
  rightClickInfo?: ClickInfo;
  transformStateOverride?: ReactZoomPanPinchState;
  tryMoveVideoRight?: (viewState: ReactZoomPanPinchState | undefined) => void;
  tryMoveVideoLeft?: (viewState: ReactZoomPanPinchState | undefined) => void;
  videoSyncRequest?: boolean;
  panMethod?: PanMethod;
};

// check for autoswitch every ms
const viewBoundaryCheckTimeMs: number = 500;
// Edge panning variables
const updateEdgePanningMs: number = 40;
const edgePanSpeed: number = 1200;
const edgePanCenterWindowOffset: number = 0.2;
const switchAtWidthRatio: number = 0.45;
// Centering variables
const updateCenterTimeMs: number = 40;
const centerStickiness: number = 0.35;

const VideoView = ({
  id,
  viewWidth,
  viewHeight,
  videoWidth,
  videoHeight,
  url,
  playTime,
  syncOffset,
  updatePlayTime,
  playbackSpeed,
  isPlaying,
  overlayItems,
  mouseOverlay,
  onMouseDown,
  onMouseUp,
  onMouseMove,
  onChangeVideoDuration,
  intervalMs,
  onVideoProgress,
  isFullscreen,
  sharedZoom,
  onSharedZoomChange,
  onChangeVideoPlaying,
  onChangeVideoLoading,
  rightClickInfo,
  transformStateOverride,
  tryMoveVideoRight,
  tryMoveVideoLeft,
  videoSyncRequest,
  panMethod,
}: Props) => {
  const classes = useStyles();

  const wrapper = useRef<ReactZoomPanPinchRef | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  //state
  const [zoom, setZoom] = useState<number>(1);
  const [videoLoading, setVideoLoading] = useState<boolean>(false);
  const [edgePanningSpeed, setEdgePanningSpeed] = useState<Coordinate | undefined>(undefined);
  const [centerCoord, setCenterCoord] = useState<Coordinate | undefined>(undefined);
  const [validVideoDimensions, setValidVideoDimensions] = useState<boolean>(false);
  const [scale, setScale] = useState<number>(1);
  const [isZooming, setIsZooming] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);

  const inViewOverlayItems: VideoOverlayObject[] = overlayItems.filter((item: VideoOverlayObject) => {
    return item.center && item.center.x > 0 && item.center.x < videoWidth && item.center.y > 0 && item.center.y < videoHeight;
  });

  //get our size ratios of window & video
  const windowSizeRatio: number = viewWidth / viewHeight;
  const resolutionRatio: number = videoWidth / videoHeight;
  
  //calculate our scaled down size based on the resolution fitting
  let contentWidth: number = 0;
  let contentHeight: number = 0;
  let scaleResMult: number = 1;
  if (windowSizeRatio > resolutionRatio) {
    contentWidth = videoWidth / (videoHeight / viewHeight);
    contentHeight = viewHeight;
    scaleResMult = videoHeight / viewHeight;
  } else {
    contentWidth = viewWidth;
    contentHeight = videoHeight / (videoWidth / viewWidth);
    scaleResMult = videoWidth / viewWidth;
  }

  const transformViewToImagePosition = useCallback(
    (x: number, y: number) => {
      //transform in-view position to image coordinate
      const leftX: number = viewWidth * 0.5 - contentWidth * 0.5;
      const rightX: number = viewWidth * 0.5 + contentWidth * 0.5;
      const topY: number = viewHeight * 0.5 - contentHeight * 0.5;
      const bottomY: number = viewHeight * 0.5 + contentHeight * 0.5;

      //bounds check
      if (x < leftX || x > rightX || y < topY || y > bottomY) return undefined;

      //get position in the image
      const imageX: number = ((x - leftX) / contentWidth) * videoWidth;
      const imageY: number = ((y - topY) / contentHeight) * videoHeight;
      return {
        x: imageX,
        y: imageY,
      };
    },
    [viewWidth, contentWidth, viewHeight, contentHeight, videoWidth, videoHeight]
  );

  const transformPositionToView = useCallback((normX: number, normY: number) => {
    return {
      x: -(((0.5 * viewWidth) - (contentWidth * 0.5) + (normX * contentWidth)) * scale) + (viewWidth * 0.5), 
      y: -(((0.5 * viewHeight) - (contentHeight * 0.5) + (normY * contentHeight)) * scale) + (viewHeight * 0.5),
    }
  }, [viewWidth, contentWidth, scale, viewHeight, contentHeight]);

  const onVideoPlay = useCallback((isPlaying: boolean) => {
    onChangeVideoPlaying && onChangeVideoPlaying(id, isPlaying);
  }, [id, onChangeVideoPlaying]);

  const onVideoLoad = useCallback((isLoading: boolean) => {
    setVideoLoading(isLoading);
    onChangeVideoLoading && onChangeVideoLoading(id, isLoading);
  }, [id, onChangeVideoLoading]);

  const onProgress = useCallback((p: ProgressState) => {
    if(syncOffset) {
      p.playedSeconds = p.playedSeconds + syncOffset;
    }
    onVideoProgress && onVideoProgress(id, p);
  }, [id, onVideoProgress, syncOffset]);
  
  const onZoomStart = useCallback((ref: ReactZoomPanPinchRef, event: MouseEvent | TouchEvent) => {
    setZoom(ref.state.scale);
    onSharedZoomChange && onSharedZoomChange(id, ref.state.scale);
    setIsZooming(true);
  }, [id, onSharedZoomChange]);

  const onZoomStop = useCallback((ref: ReactZoomPanPinchRef, event: MouseEvent | TouchEvent) => {
    setZoom(ref.state.scale);
    onSharedZoomChange && onSharedZoomChange(id, ref.state.scale);
    setIsZooming(false);
  }, [id, onSharedZoomChange]);

  const onZoomChange = useCallback((ref: ReactZoomPanPinchRef, event: MouseEvent | TouchEvent) => {
    setZoom(ref.state.scale);
    onSharedZoomChange && onSharedZoomChange(id, ref.state.scale);
  }, [id, onSharedZoomChange]);
  
  /*
  const resetView = useCallback(() => {
    wrapper.current && wrapper.current?.centerView();
  }, []);
  */

  const onRootMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if(panMethod === PanMethod.Edge && rootRef.current) {
        const rect: DOMRect = rootRef.current?.getBoundingClientRect();
        // Normalize x and y from -.5 to .5
        let xNormalized: number = -0.5 + (event.clientX - rect.left) / rect.width;
        let yNormalized: number = -0.5 + (event.clientY - rect.top) / rect.height;
        
        // Add offset to create a non-panning zone in the center.
        if(xNormalized < -edgePanCenterWindowOffset) {
          xNormalized += edgePanCenterWindowOffset;
        } else if(xNormalized > edgePanCenterWindowOffset) {
          xNormalized -= edgePanCenterWindowOffset;
        } else {
          xNormalized = 0
        }
        
        if(yNormalized < -edgePanCenterWindowOffset) {
          yNormalized += edgePanCenterWindowOffset;
        } else if(yNormalized > edgePanCenterWindowOffset) {
          yNormalized -= edgePanCenterWindowOffset;
        } else {
          yNormalized = 0
        }

        setEdgePanningSpeed({
          x: xNormalized*(xNormalized < 0 ? xNormalized*-1 : xNormalized)*edgePanSpeed,
          y: yNormalized*(yNormalized < 0 ? yNormalized*-1 : yNormalized)*edgePanSpeed,
        });
      }
    },
    [panMethod]
  );

  const onRootMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setEdgePanningSpeed(undefined);
    },
    []
  );

  const onViewMouseMove = useCallback(
    (mousePosition: Coordinate) => {
      onMouseMove && onMouseMove(id, mousePosition);
    },
    [id, onMouseMove]
  );

  const onViewMouseDown = useCallback((mousePosition: Coordinate, event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    // Left mouse button
    if(event.button === 0) {
      if (!onMouseDown) return;
      event.preventDefault();
      const mousePageCoord: Coordinate = {x: event.nativeEvent.pageX, y: event.nativeEvent.pageY};
      onMouseDown(id, {
        button: MouseButton.LeftMouseButton,
        x: mousePosition.x,
        y: mousePosition.y,
        pageX: mousePageCoord.x,
        pageY: mousePageCoord.y,
        clickTime: 0,
        longPress: false,
      });
    }
  }, [onMouseDown, id]);

  const onViewMouseUp = useCallback((mousePosition: Coordinate, event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    // Left mouse button
    if(event.button === 0) {
      if (!onMouseUp) return;
      event.preventDefault();
      const mousePageCoord: Coordinate = {x: event.nativeEvent.pageX, y: event.nativeEvent.pageY};
      onMouseUp(id, {
        button: MouseButton.LeftMouseButton,
        x: mousePosition.x,
        y: mousePosition.y,
        pageX: mousePageCoord.x,
        pageY: mousePageCoord.y,
        clickTime: 0,
        longPress: false,
      });
    }
  }, [id, onMouseUp]);

  const handleChangeVideoDuration = useCallback((duration: number) => {
    // if we have a sync offset, subtract it from our duration.
    onChangeVideoDuration && onChangeVideoDuration(duration, syncOffset || 0);
  }, [onChangeVideoDuration, syncOffset]);

  const handleRightClick = useCallback((clickInfo: ClickInfo) => {
    if(!rootRef.current) return;
    // Lets check if this click was in this view first
    const rect: DOMRect = rootRef.current?.getBoundingClientRect();
    const clickInView: boolean = clickInfo.pageX > rect.left 
      && clickInfo.pageY > rect.top
      && clickInfo.pageX < rect.right
      && clickInfo.pageY < rect.bottom;
    // Don't continue if click wasnt in view
    if(!clickInView) {
      return;
    }

    const imagePos: Coordinate | undefined = transformViewToImagePosition(
      clickInfo.x,
      clickInfo.y,
    );

    if(imagePos) {
      // Only send click if it was a valid click on the image
      onMouseDown && onMouseDown(id, {
        button: MouseButton.RightMouseButton,
        x: imagePos.x,
        y: imagePos.y,
        pageX: clickInfo.pageX,
        pageY: clickInfo.pageY,
        clickTime: clickInfo.clickTime,
        longPress: clickInfo.longPress,
      });
    }
  }, [id, onMouseDown, transformViewToImagePosition]);

  const limitCoordinateToContent = useCallback((x: number, y: number) => {
    if(!wrapper.current?.instance.bounds) {
      return {x, y};
    }

    const scaledViewWidth: number = viewWidth * wrapper.current.state.scale;
    const scaledContentWidth: number = contentWidth * wrapper.current.state.scale;
    const minX: number = wrapper.current.instance.bounds.minPositionX+(scaledViewWidth*0.5)-(scaledContentWidth*0.5);
    const maxX: number = wrapper.current.instance.bounds.maxPositionX-(scaledViewWidth*0.5)+(scaledContentWidth*0.5);

    const newX: number = Math.max(minX, Math.min(maxX, x));

    const scaledViewHeight: number = viewHeight * wrapper.current.state.scale;
    const scaledContentHeight: number = contentHeight * wrapper.current.state.scale;
    const minY: number = wrapper.current.instance.bounds.minPositionY+(scaledViewHeight*0.5)-(scaledContentHeight*0.5);
    const maxY: number = wrapper.current.instance.bounds.maxPositionY-(scaledViewHeight*0.5)+(scaledContentHeight*0.5);

    const newY: number = Math.max(minY, Math.min(maxY, y));

    return {x: newX, y: newY};
  }, [contentHeight, contentWidth, viewHeight, viewWidth]);

  useInterval(() => {
    if(!videoSyncRequest && edgePanningSpeed && wrapper.current && !isZooming) {
      wrapper.current.setTransform(
        wrapper.current.state.positionX-edgePanningSpeed.x, 
        wrapper.current.state.positionY-edgePanningSpeed.y, 
        wrapper.current.state.scale, 
        60,
        "linear",
      );
    }
  }, updateEdgePanningMs);

  // Try moving video to the left or right when crossing switchViewAt boundary
  useInterval(() => {
    if(
      !isFullscreen ||
      !wrapper.current?.instance.bounds ||
      videoLoading ||
      isZooming
    ) {
      return;
    }
    
    if(panMethod === PanMethod.Center && centerCoord) {
      // If we're centering on a coordinate, we can simply check our normalized x
      if(centerCoord.x < 0 && tryMoveVideoLeft) {
        tryMoveVideoLeft(undefined);
      } else if(centerCoord.x > 1 && tryMoveVideoRight) {
        tryMoveVideoRight(undefined);
      }
    } else {
      // If we're not centering - we check our view state.
      const x: number = wrapper.current.state.positionX;
      const min: number = wrapper.current.instance.bounds.minPositionX;
      const max: number = wrapper.current.instance.bounds.maxPositionX;
  
      const switchAtWidth: number = viewWidth * switchAtWidthRatio;
  
      const scaledViewWidth: number = viewWidth * wrapper.current.state.scale;
      const scaledContentWidth: number = contentWidth * wrapper.current.state.scale;
      const minVidBorder: number = min+(scaledViewWidth*0.5)-(scaledContentWidth*0.5);
      const maxVidBorder: number = max-(scaledViewWidth*0.5)+(scaledContentWidth*0.5);
      
      if(x < minVidBorder && tryMoveVideoRight) {
        // Exceeded Right Border
        const diffX: number = minVidBorder - x;
        // We check at a certain width ratio
        if(diffX > switchAtWidth) {
          tryMoveVideoRight({
            ...wrapper.current.state,
              positionX: maxVidBorder + switchAtWidth,
          });
        }
      } else if(x > maxVidBorder && tryMoveVideoLeft) {
        // Exceeded Left Border
        const diffX: number = x - maxVidBorder;
        // We check at a certain width ratio
        if(diffX > switchAtWidth) {      
          tryMoveVideoLeft({
            ...wrapper.current.state,
            positionX: minVidBorder - switchAtWidth,
          });
        }
      }
    }
  }, viewBoundaryCheckTimeMs);

  useInterval(() => {
    if(panMethod === PanMethod.Center || centerCoord !== undefined) {
      // Update our center coordinate every updateCenterTimeMs
      const centerImageCoord: Coordinate | undefined = overlayItems.find(x=>x.centerViewOnObject)?.center;
      const newCenterCoord: Coordinate | undefined = panMethod === PanMethod.Center && centerImageCoord 
        ? {
            x: centerImageCoord.x / videoWidth, 
            y: centerImageCoord.y / videoHeight
          }
        : undefined;
      
      setCenterCoord(newCenterCoord);
    }
  }, updateCenterTimeMs);

  // When edge panning is disabled, we reset our view.
  useEffect(() => {
    if(panMethod !== PanMethod.Edge && edgePanningSpeed) {
      setEdgePanningSpeed(undefined);
      //resetView();
    }
  }, [panMethod, edgePanningSpeed]); 

  // Trigger right click info handling
  useEffect(() => {
    if(rightClickInfo){
      handleRightClick(rightClickInfo);
    } 
  }, [handleRightClick, rightClickInfo]);
  
  // Set valid video dimensions when width or height of video is known
  useEffect(() => {
    setValidVideoDimensions(videoWidth !== 0 && videoHeight !== 0);
  }, [videoHeight, videoWidth]);

  // When new view state, set our new transform
  useEffect(() => {
    if(!initialized && transformStateOverride && wrapper?.current) {
      wrapper.current.setTransform(transformStateOverride.positionX, transformStateOverride.positionY, transformStateOverride.scale);
      setInitialized(true);
    }
  }, [initialized, transformStateOverride])

  // Centering view on object
  useLayoutEffect(() => {
    if(panMethod !== PanMethod.Center || !centerCoord || !wrapper?.current || isZooming) return;

    if(scale === 1) {
      wrapper.current.instance.setCenter();
      return;
    }

    const nC: Coordinate = transformPositionToView(centerCoord.x, centerCoord.y);

    // Else we smoothly transition towards our new position.
    const currX: number = wrapper.current.state.positionX;
    const currY: number = wrapper.current.state.positionY;
    const diffX: number = (nC.x - currX) * centerStickiness;
    const diffY: number = (nC.y - currY) * centerStickiness;

    // Stop calling setTransform when we're close enough
    if(Math.abs(diffX) < 1 && Math.abs(diffY) < 1) return;

    const fitCoord: Coordinate = limitCoordinateToContent(currX+diffX, currY+diffY);
    wrapper.current.setTransform(fitCoord.x, fitCoord.y, scale, updateCenterTimeMs, "linear");
  }, [centerCoord, panMethod, isZooming, limitCoordinateToContent, scale, transformPositionToView]);

  // Update scale (needs to be layouteffect)
  useLayoutEffect(() => {
    const newScale: number = sharedZoom ? sharedZoom : zoom;
    setScale(newScale);
  }, [sharedZoom, zoom]);

  // Return message when video dimensions are incorrect.
  if (!validVideoDimensions) {
    return (
      <Typography>
        Camera frame width and/or height is 0. <br />
        Please set the right dimensions for camera: {id}
      </Typography>
    );
  }

  return (<div 
    className={classes.root} 
    ref={rootRef} 
    onMouseMove={onRootMouseMove}
    onMouseLeave={onRootMouseLeave}
  >
    <TransformWrapper
      ref={wrapper}
      disabled={ panMethod === undefined }
      minScale={1}
      maxScale={25}
      limitToBounds={ centerCoord === undefined }
      doubleClick={{ 
        disabled: true,
      }}
      panning={{ 
        disabled: panMethod !== PanMethod.Drag,
        //activationKeys: ['Shift']
      }}
      onZoomStart={onZoomStart}
      onZoomStop={onZoomStop}
      onZoom={onZoomChange}
    >
      <TransformComponent>
        <VideoPlayer
          id={id}
          width={viewWidth}
          height={viewHeight}
          url={url}
          isPlaying={isPlaying}
          seekTime={updatePlayTime ? playTime : undefined}
          playbackSpeed={playbackSpeed}
          onVideoPlay={onVideoPlay}
          onVideoLoad={onVideoLoad}
          onDuration={handleChangeVideoDuration}
          onProgress={onProgress}
          intervalMs={intervalMs}
        />
        <ViewOverlayCanvas
          width={videoWidth}
          height={videoHeight}
          svgScale={1/(wrapper.current?.state.scale||1) * scaleResMult}
          onMouseMove={onViewMouseMove}
          onMouseDown={onViewMouseDown}
          onMouseUp={onViewMouseUp}
          overlayObjects={inViewOverlayItems}
          mouseObjects={mouseOverlay}
          viewScaleFirefox={(wrapper.current?.state.scale||1)}
          xOffsetFirefox={ viewWidth > contentWidth ? (viewWidth - contentWidth) * 0.5 : undefined }
          yOffsetFirefox={ viewHeight > contentHeight ? (viewHeight - contentHeight) * 0.5 : undefined }
        />
      </TransformComponent>
    </TransformWrapper>
  </div>);
};

export default VideoView;
